import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  interval,
  Subscription,
  fromEvent,
  merge,
  timer,
  throwError,
  of,
} from 'rxjs';
import {
  take,
  timeout,
  catchError,
  switchMap,
  map,
  filter,
  debounceTime,
} from 'rxjs/operators';
import { SocketService } from '../../event/services/socket.service';
import { DatadogService } from '../../observability/datadog.service';
import {
  ConnectionState,
  ConnectionConfig,
  ConnectionLogger,
  ConnectionMetrics,
} from '../models/connection-state.model';
import { environment } from '../../../../environments/environment';

/**
 * Service responsável por monitorar a saúde da conexão WebSocket
 * 
 * Responsabilidades:
 * - Enviar heartbeat (ping) periodicamente
 * - Detectar falhas de conexão
 * - Emitir mudanças de estado de conexão
 * - Detectar Page Visibility (tab background/foreground)
 * - Detectar Network Online/Offline
 * 
 * Princípios SOLID:
 * - SRP: Responsabilidade única de monitorar conexão
 * - OCP: Configurável através de ConnectionConfig
 * - DIP: Depende de abstração (SocketService)
 * 
 * MELHORIAS:
 * ✅ Page Visibility API (detecta Moblin background)
 * ✅ Network Online/Offline API
 * ✅ Logs configuráveis
 * ✅ Métricas de conexão
 * ✅ Guard contra múltiplas inicializações
 */
@Injectable({
  providedIn: 'root',
})
export class ConnectionMonitorService {
  private readonly config: ConnectionConfig = environment.connection;
  
  private readonly logger: ConnectionLogger;
  
  private connectionStateSubject = new BehaviorSubject<ConnectionState>('disconnected');
  private heartbeatSubscription?: Subscription;
  private visibilitySubscription?: Subscription;
  private networkSubscription?: Subscription;
  private consecutiveFailures = 0;
  private isMonitoring = false;
  
  // Métricas
  private metrics: ConnectionMetrics = {
    totalReconnections: 0,
    totalDisconnections: 0,
    currentAttempt: 0,
    totalDowntime: 0,
    averageReconnectTime: 0,
  };
  private lastDisconnectTimestamp?: number;
  
  /**
   * Observable do estado atual da conexão
   */
  public readonly connectionState$: Observable<ConnectionState> =
    this.connectionStateSubject.asObservable();
  
  /**
   * Observable booleano de conexão (convenience)
   */
  public readonly isConnected$: Observable<boolean> = this.connectionState$.pipe(
    map(state => state === 'connected')
  );

  constructor(
    private socketService: SocketService,
    private datadogService: DatadogService
  ) {
    this.logger = new ConnectionLogger(this.config.enableDebugLogs, this.datadogService);
    this.setupBrowserAPIs();
  }

  /**
   * Configura APIs do navegador (Page Visibility + Network Status)
   */
  private setupBrowserAPIs(): void {
    // 🔴 CRÍTICO: Page Visibility API (detecta Moblin background)
    if (typeof document !== 'undefined' && 'hidden' in document) {
      this.visibilitySubscription = fromEvent(document, 'visibilitychange')
        .pipe(debounceTime(100))
        .subscribe(() => {
          if (document.hidden) {
            this.logger.log('📱 Tab foi para background (Moblin suspenso?)');
            // Não fazer nada, deixar heartbeat detectar
          } else {
            this.logger.log('📱 Tab voltou para foreground - verificando conexão...');
            this.onPageResumed();
          }
        });
    }

    // 🔴 CRÍTICO: Network Online/Offline API
    if (typeof window !== 'undefined' && 'navigator' in window) {
      this.networkSubscription = merge(
        fromEvent(window, 'online'),
        fromEvent(window, 'offline')
      ).subscribe((event) => {
        const isOnline = event.type === 'online';
        
        if (isOnline) {
          this.logger.log('📶 Rede voltou - tentando reconectar...');
          this.onNetworkRestored();
        } else {
          this.logger.warn('📡 Rede offline detectada');
          this.onNetworkLost();
        }
      });
    }
  }

  /**
   * Inicia o monitoramento de conexão
   * 
   * MELHORIA: Guard contra múltiplas inicializações
   */
  startMonitoring(): void {
    if (this.isMonitoring) {
      this.logger.warn('⚠️ Monitoramento já está ativo');
      return;
    }

    this.logger.log('🔌 Iniciando monitoramento de conexão WebSocket');
    
    // CRÍTICO: Parar heartbeat anterior ANTES de marcar como monitoring
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = undefined;
    }
    
    // Resetar estado
    this.consecutiveFailures = 0;
    this.connectionStateSubject.next('connected');
    this.isMonitoring = true;
    
    // Iniciar heartbeat periódico
    this.heartbeatSubscription = interval(this.config.heartbeatInterval)
      .pipe(
        filter(() => this.isMonitoring), // Apenas se monitoramento ativo
        switchMap(() => this.sendPing())
      )
      .subscribe({
        next: () => {
          // Ping bem-sucedido
          if (this.consecutiveFailures > 0) {
            this.logger.log('✅ Conexão recuperada');
            this.consecutiveFailures = 0;
            this.connectionStateSubject.next('connected');
          }
        },
        error: (error) => {
          this.handlePingFailure(error);
        },
      });
  }

  /**
   * Para o monitoramento de conexão
   */
  stopMonitoring(): void {
    this.logger.log('🛑 Parando monitoramento');
    
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = undefined;
    }
    
    this.isMonitoring = false;
  }

  /**
   * Cleanup completo (destroy)
   */
  destroy(): void {
    this.logger.log('🗑️ Destruindo ConnectionMonitorService');
    
    this.stopMonitoring();
    
    if (this.visibilitySubscription) {
      this.visibilitySubscription.unsubscribe();
    }
    
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
  }

  /**
   * Obtém o estado atual da conexão
   */
  getCurrentState(): ConnectionState {
    return this.connectionStateSubject.value;
  }

  /**
   * Força mudança de estado (usado pelo ReconnectionService)
   */
  setConnectionState(state: ConnectionState): void {
    if (this.connectionStateSubject.value !== state) {
      this.logger.log(`🔄 Estado de conexão mudou: ${this.connectionStateSubject.value} → ${state}`);
      this.connectionStateSubject.next(state);
      
      // Atualizar métricas
      if (state === 'disconnected') {
        this.metrics.totalDisconnections++;
        this.lastDisconnectTimestamp = Date.now();
      } else if (state === 'connected' && this.lastDisconnectTimestamp) {
        this.metrics.totalReconnections++;
        const downtime = Date.now() - this.lastDisconnectTimestamp;
        this.metrics.totalDowntime += downtime;
        this.metrics.averageReconnectTime = 
          this.metrics.totalDowntime / this.metrics.totalReconnections;
      }
      
      // Se conectou, resetar falhas
      if (state === 'connected') {
        this.consecutiveFailures = 0;
      }
    }
  }

  /**
   * Reseta o contador de falhas (chamado após reconexão bem-sucedida)
   */
  resetFailures(): void {
    this.consecutiveFailures = 0;
  }

  /**
   * Obtém métricas de conexão
   */
  getMetrics(): ConnectionMetrics {
    return { ...this.metrics };
  }

  /**
   * Trata evento de página resumida (voltou de background)
   * 
   * CRÍTICO: Moblin/Safari suspende tab, ao voltar precisamos verificar imediatamente
   * 
   * CORREÇÃO: NÃO fazer ping imediatamente, apenas emitir estado desconectado
   * se detectar que socket perdeu conexão. O heartbeat normal vai detectar
   * se a conexão está OK, ou o sistema de reconexão vai cuidar disso.
   */
  private onPageResumed(): void {
    this.logger.log('📱 Tab voltou para foreground - verificando conexão...');
    
    // APENAS verificar se socket está conectado
    // NÃO fazer ping - deixar o heartbeat normal ou reconexão cuidar disso
    if (!this.socketService.isConnected()) {
      this.logger.warn('⚠️ Desconexão detectada após voltar de background');
      this.connectionStateSubject.next('disconnected');
    } else {
      this.logger.log('✅ Socket ainda conectado após voltar de background');
      // Socket diz que está conectado, heartbeat normal vai validar
    }
  }

  /**
   * Trata evento de rede restaurada
   */
  private onNetworkRestored(): void {
    // Se estava desconectado, emitir evento para tentar reconectar
    if (this.getCurrentState() === 'disconnected') {
      this.logger.log('🔄 Rede restaurada, sinalizando para reconectar...');
      // O WebSocketHealthService vai detectar e tentar reconectar
    }
  }

  /**
   * Trata evento de rede perdida
   */
  private onNetworkLost(): void {
    this.logger.warn('📡 Rede offline detectada - marcando como desconectado');
    
    // PRIMEIRO emitir desconectado (para websocket-health reagir)
    this.connectionStateSubject.next('disconnected');
    
    // DEPOIS parar heartbeat
    if (this.isMonitoring) {
      this.logger.log('💤 Pausando heartbeat (rede offline)');
      this.stopMonitoring();
    }
  }

  /**
   * Envia um ping e aguarda pong com timeout
   */
  private sendPing(): Observable<any> {
    return this.socketService
      .ping() // ✅ Usa o método ping() dedicado
      .pipe(
        timeout(this.config.heartbeatTimeout),
        catchError((error) => {
          throw new Error('Ping timeout');
        })
      );
  }

  /**
   * Trata falha de ping
   */
  private handlePingFailure(error: any): void {
    this.consecutiveFailures++;
    
    this.logger.warn(
      `⚠️ Ping falhou (${this.consecutiveFailures}/${this.config.maxHeartbeatFailures}): ${error.message}`
    );

    // Se atingiu limite de falhas, considerar desconectado
    if (this.consecutiveFailures >= this.config.maxHeartbeatFailures) {
      this.logger.error('❌ Conexão perdida - limite de falhas atingido');
      this.connectionStateSubject.next('disconnected');
      this.stopMonitoring(); // Para de fazer ping enquanto desconectado
    }
  }
}
